import axios from "@/axios";
import callApi from "@/call-api-axios";

/**
 * 콜봇 내선 관리(내선·전화기·설치 장소) call-api 호출.
 * 요청 문서: ~/md/front/admin-콜봇-내선관리-화면-요청.md
 * 서버 계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "요청 계약"
 *           (정본 ~/md/back/콜봇-내선-7단계-프로비저닝-요청.md)
 *
 * - 모든 메서드의 첫 인자는 `companyId`(= inapi `companies._id`)다. admin 은 병원을 골라야
 *   하므로 studio 와 달리 경로의 회사를 호출부가 넘긴다.
 * - `callApi` 인스턴스가 `response.data` 를 언랩하므로 각 메서드는 응답 본문을 바로 resolve 한다.
 * - 응답은 snake_case, `_at` 은 unix ms. 아래 @typedef 는 studio `api/agent/types.ts` 의 발췌다.
 * - 병원 검색(`searchHospitals`)만 inapi 인스턴스(`@/axios`)를 쓴다 — 응답은 언랩되지 않는다.
 */

// ---------------------------------------------------------------------------
// 응답 형태 (JSDoc) — PBX 내선(4단계)
// ---------------------------------------------------------------------------

/**
 * `retired`·`locked`·`blocked` 는 서버 소유 검사가 받지 않는다 — 드롭다운에서 뺀다.
 * `disabled`(일시 중지)는 7단계에서 생긴다 — 되돌릴 수 있고 상담사 번호 행은 남는다.
 * @typedef {'active'|'disabled'|'locked'|'retired'|'blocked'|'legacy'} PbxExtensionStatus
 */
/** `n/a` = 레거시 회선(PBX 에 정적으로 정의돼 call-api 가 반영하지 않음)
 * @typedef {'pending'|'synced'|'partial'|'error'|'n/a'} PbxExtensionSyncState */
/** @typedef {'reachable'|'unreachable'|'unregistered'|'unknown'} PbxExtensionRegistrationStatus */
/** @typedef {'pending'|'put_confirmed'|'awaiting_phone'|'confirmed'|'failed'} PbxExtensionRotationState */
/** @typedef {'manual'|'unlock'|'device_replace'|'device_unassign'} PbxExtensionRotationReason */
/** 설정 재수신(check-sync) 전달 결과
 * @typedef {'queued'|'no_contact'|'in_call'|'unavailable'|'failed'} PbxNotifyOutcome */

/**
 * 진행 중이거나 막 끝난 시크릿 회전. 끝난 회전은 다음 조회에서 null 이 된다.
 * @typedef {Object} PbxExtensionRotation
 * @property {PbxExtensionRotationState} state
 * @property {PbxExtensionRotationReason|string} reason
 * @property {number} started_at unix ms
 * @property {number|null} promoted_at unix ms
 * @property {{ at: number|null, outcome: PbxNotifyOutcome|string|null }} notify
 */

/**
 * 내선 등록 상태(호스트 스냅샷). 자격증명·전화기 공인 IP 원문은 절대 오지 않는다.
 * @typedef {Object} PbxExtensionRegistration
 * @property {PbxExtensionRegistrationStatus} status
 * @property {'udp'|'tcp'|'tls'|null} transport
 * @property {string|null} contact_host_masked 'a.b.x.x' 또는 '***'
 * @property {string|null} user_agent
 * @property {number|null} observed_at unix ms
 * @property {'event'|'resync'|'delete'|'provision'|'snapshot'|null} source
 * @property {number|null} [rtt_ms] 7단계
 */

/**
 * GET /pbx-extensions 항목. 7단계 필드는 4단계 call-api 가 남은 환경에서도 목록이
 * 깨지지 않게 전부 optional.
 * @typedef {Object} PbxExtensionItem
 * @property {string} id
 * @property {string} number
 * @property {PbxExtensionStatus} status
 * @property {'issued'|'adopted'|'legacy'} origin
 * @property {PbxExtensionSyncState} sync_state
 * @property {string|null} last_error_code
 * @property {string|null} counselor_number_id 연결된 상담사 번호 행 — 한 내선은 한 행에만 붙는다. null 이면 고를 수 있다
 * @property {PbxExtensionRegistration|null} registration
 * @property {string|null} [device_id] 연결된 전화기(PbxDeviceItem.id)
 * @property {number|null} [secret_rotated_at] unix ms
 * @property {number|null} [disabled_at] unix ms
 * @property {PbxExtensionRotation|null} [rotation]
 * @property {number|null} [checked_at] unix ms — 호스트 스냅샷 시각("마지막 확인"). observed_at(상태 변경 시각)과 다르다
 */

/** @typedef {{ items: PbxExtensionItem[] }} PbxExtensionListResponse */

/**
 * 조작 응답에 붙는 PBX 반영 경고 — 4단계 counselor-number warnings 와 같은 형태.
 * PENDING 은 서버가 15초 간격으로 재시도하고, ERROR 는 재시도가 끝났거나 호스트가 종결
 * 거부한 것이라 사람이 조치해야 한다. 둘을 같은 문구로 보이면 안 된다.
 * @typedef {Object} CounselorNumberWarning
 * @property {'PBX_EXTENSION_SYNC_PENDING'|'PBX_EXTENSION_SYNC_ERROR'} code
 * @property {string|null} detail 마지막 pbx-ami 오류 코드. 아직 기록된 것이 없으면 null
 */

/** @typedef {PbxExtensionItem & { warnings?: CounselorNumberWarning[] }} PbxExtensionActionResponse */

/**
 * POST pbx-extensions/:extId/release
 * @typedef {Object} PbxExtensionReleaseResponse
 * @property {string} id
 * @property {string} number
 * @property {boolean} released
 * @property {number} released_at unix ms
 */

/**
 * check-sync 응답 — 항상 200. `notified:true` 는 "전달 시도" 이지 "적용 확인" 이 아니다.
 * `reason` 은 에러 코드가 아니다(pbx-ami 의 202/409 를 접은 값).
 * @typedef {Object} PbxCheckSyncResponse
 * @property {boolean} notified
 * @property {number|null} contacts
 * @property {null|'EXTENSION_NOT_REGISTERED'|'EXTENSION_IN_CALL'} reason
 */

// ---------------------------------------------------------------------------
// 응답 형태 (JSDoc) — 상담사 번호(counselor-number 응답의 `pbx_extension` 포함)
// ---------------------------------------------------------------------------

/**
 * counselor-number 응답의 `pbx_extension`
 * @typedef {Object} PbxExtensionSummary
 * @property {string} id
 * @property {string} number
 * @property {PbxExtensionStatus} status
 * @property {PbxExtensionSyncState} sync_state
 * @property {PbxExtensionRegistrationStatus|null} registration_status 갓 발급한 내선은 전화기가 붙기 전까지 null(확인 중)
 * @property {'udp'|'tcp'|'tls'|null} [transport] 7단계
 * @property {number|null} [registration_observed_at] unix ms
 * @property {number|null} [checked_at] unix ms — 호스트 스냅샷 시각("마지막 확인")
 * @property {string|null} [device_id]
 */

/**
 * GET /counselor-numbers 항목
 * @typedef {Object} CounselorNumber
 * @property {string} id
 * @property {string} name 번호 이름 (예: 착신번호 1)
 * @property {string} number 하이픈 없는 숫자 문자열
 * @property {string|null} [account_id] 연결된 계정 — null 이면 미지정
 * @property {boolean} [is_call_forwarding] 착신전환용 번호 여부
 * @property {boolean} [is_ring_group] 링그룹 번호 여부(is_call_forwarding 과 상호배타)
 * @property {boolean} [is_extension] 내선번호 여부 — true 면 number 를 PBX 내선(숫자 4자리)으로 취급
 * @property {string|null} [status] 상담사 상태 — 미배포 구간은 부재
 * @property {boolean} [auto_after_call] 자동 후처리 전환 여부 — 판정은 항상 `!== false`
 * @property {PbxExtensionSummary|null} [pbx_extension] 연결된 PBX 내선(4단계). null 이면 일반 번호이거나 4단계 이전 내선 행
 * @property {number} [created_at] unix ms
 * @property {number} [updated_at] unix ms
 */

/** @typedef {{ items: CounselorNumber[] }} CounselorNumberListResponse */

// ---------------------------------------------------------------------------
// 응답 형태 (JSDoc) — PBX 전화기·설치 장소(7단계)
// ---------------------------------------------------------------------------

/** @typedef {'unassigned'|'assigned'|'returned'} PbxDeviceStatus */
/** @typedef {'never'|'stale'|'current'} PbxProvisioningState */
/** @typedef {'tls'|'udp'} PbxSipTransport */

/**
 * @typedef {Object} PbxDeviceItem
 * @property {string} id
 * @property {string} site_id
 * @property {string} mac 12 hex 소문자, 구분자 없음
 * @property {string|null} label
 * @property {string|null} model
 * @property {PbxDeviceStatus} status
 * @property {string|null} extension_id
 * @property {string|null} counselor_number_id
 * @property {PbxSipTransport|null} sip_transport null = 설치 장소 기본값
 * @property {number|null} sip_port
 * @property {boolean|null} srtp_require
 * @property {{ at: number, ip_masked: string|null }|null} first_seen
 * @property {{ at: number, ip_masked: string|null, user_agent: string|null, file: string|null, token_version: number|null }|null} last_provision
 * @property {number|null} admin_password_rotated_at
 * @property {{ expires_at: number, used_at: number|null }|null} recovery_code
 * @property {PbxProvisioningState} provisioning_state
 * @property {number|null} assigned_at
 * @property {number} created_at
 * @property {number} updated_at
 * @property {string|null} [user_agent] 서버 규격상 목록 항목에 없을 수 있다 — first_seen 이나 last_provision 쪽 UA 를 쓴다
 */

/** @typedef {{ items: PbxDeviceItem[] }} PbxDeviceListResponse */
/** @typedef {{ status?: PbxDeviceStatus, site_id?: string }} ListPbxDevicesParams */
/** @typedef {PbxDeviceItem & { warnings?: CounselorNumberWarning[] }} PbxDeviceActionResponse */
/** @typedef {{ site_id: string, mac: string, label?: string }} CreatePbxDeviceRequest */
/** @typedef {{ label?: string|null, site_id?: string }} UpdatePbxDeviceRequest */
/** @typedef {{ counselor_number_id: string, replace?: boolean }} ClaimPbxDeviceRequest */
/** @typedef {{ sip_transport: PbxSipTransport, sip_port?: number, srtp_require?: boolean }} UpdatePbxDeviceTransportRequest */

/**
 * @typedef {Object} PbxRecoveryCodeResponse
 * @property {string} code 6자 대문자
 * @property {number} expires_at unix ms
 * @property {string} recovery_url 전화기 Server Address 에 넣을 값 — 화면은 조립하지 않는다
 */

/**
 * @typedef {Object} PbxDeviceLogItem
 * @property {string} id
 * @property {string} name
 * @property {number} size
 * @property {number} uploaded_at unix ms
 */
/** @typedef {{ items: PbxDeviceLogItem[] }} PbxDeviceLogListResponse */

/** @typedef {'active'|'revoked'} PbxSiteStatus */

/**
 * @typedef {Object} PbxSiteItem
 * @property {string} id
 * @property {string} name
 * @property {PbxSiteStatus} status
 * @property {string} sip_server_fqdn 읽기 전용 — PBX 호스트의 속성
 * @property {PbxSipTransport} sip_transport_default
 * @property {number} sip_port_default
 * @property {boolean} srtp_require
 * @property {boolean} tls_verify_wildcard_cert
 * @property {string} ntp_server
 * @property {number} gmt_offset_sec
 * @property {number} polling_period_sec
 * @property {string[]} public_ips
 * @property {boolean} restrict_provisioning_to_site_ips
 * @property {number} token_version
 * @property {number|null} previous_token_valid_until unix ms
 * @property {number} unmigrated_device_count last_provision.token_version < token_version 인 전화기 수(서버 계산)
 * @property {number} created_at
 * @property {number} updated_at
 */

/** @typedef {{ items: PbxSiteItem[] }} PbxSiteListResponse */

/**
 * @typedef {Object} CreatePbxSiteRequest
 * @property {string} name
 * @property {PbxSipTransport} [sip_transport_default]
 * @property {number} [sip_port_default]
 * @property {boolean} [srtp_require]
 * @property {string} [ntp_server]
 * @property {number} [gmt_offset_sec]
 * @property {number} [polling_period_sec]
 * @property {string[]} [public_ips]
 * @property {boolean} [restrict_provisioning_to_site_ips]
 */
/** @typedef {Partial<CreatePbxSiteRequest>} UpdatePbxSiteRequest */
/** 201 — provisioning_url 은 이 응답에서만 온다
 * @typedef {PbxSiteItem & { provisioning_url: string }} CreatePbxSiteResponse */
/** @typedef {{ grace_hours?: number, revoke_previous?: boolean }} RotatePbxSiteTokenRequest grace_hours 기본 72, revoke_previous 기본 false(오프보딩 스위치) */
/**
 * @typedef {Object} RotatePbxSiteTokenResponse
 * @property {string} provisioning_url
 * @property {number} token_version
 * @property {number|null} previous_token_valid_until unix ms
 */
/** @typedef {{ provisioning_url: string }} PbxProvisioningUrlResponse */

// ---------------------------------------------------------------------------
// 경로 헬퍼 — 회사 id 를 인자로 받는다
// ---------------------------------------------------------------------------

const companyBase = (companyId) => `/api/v1/companies/${encodeURIComponent(companyId)}/callbot`;

const counselorExtensionBase = (companyId, counselorNumberId) =>
  `${companyBase(companyId)}/counselor-numbers/${encodeURIComponent(counselorNumberId)}/extension`;

const deviceBase = (companyId, deviceId) =>
  `${companyBase(companyId)}/pbx-devices/${encodeURIComponent(deviceId)}`;

const siteBase = (companyId, siteId) =>
  `${companyBase(companyId)}/pbx-sites/${encodeURIComponent(siteId)}`;

export const callbotApi = {
  // -------------------------------------------------------------------------
  // 상담사 번호 / 번호 그룹 (call-api 자체 DB, 사용자 관리)
  // 백엔드 구현 완료(~/md/front/콜봇-상담사번호-번호그룹-백엔드완료.md), 배포 시점 미정
  // — 배포 전까지는 여전히 404/501 가능하므로 호출부 graceful 유지 필요.
  // -------------------------------------------------------------------------

  /**
   * GET /counselor-numbers — 상담사 번호 목록 (call-api 자체 DB, 사용자 관리).
   * 계약: ~/md/front/콜봇-상담사번호-번호그룹-백엔드완료.md — 미배포 구간은 호출부 graceful.
   * @param {string} companyId
   * @returns {Promise<CounselorNumberListResponse>}
   */
  listCounselorNumbers(companyId) {
    return callApi.get(`${companyBase(companyId)}/counselor-numbers`);
  },

  /**
   * GET /pbx-extensions — 회사의 미해제 내선 전부(retired·locked 포함, 최신순).
   * 드롭다운 후보 거르기는 호출부 몫이다(service/pbxExtensions.js).
   * 4단계 이전 call-api 에는 라우트가 없어 404 NOT_FOUND — 호출부가
   * isCallApiUnimplemented 로 감지해 보유 내선 선택을 숨긴다.
   * 계약: ~/md/front/studio-콜봇-내선-자동발급-요청.md "요청 계약" 5
   * @param {string} companyId
   * @returns {Promise<PbxExtensionListResponse>}
   */
  listPbxExtensions(companyId) {
    return callApi.get(`${companyBase(companyId)}/pbx-extensions`);
  },

  // -------------------------------------------------------------------------
  // 내선 관리(7단계) — 내선 생명주기·전화기·설치 장소. 화면: ExtensionManage.
  // 조작은 전부 멱등 키가 없다 — 호출부가 응답 전까지 버튼을 막는다.
  // 7단계 전 call-api 는 라우트가 없어 404 NOT_FOUND(isCallApiUnimplemented).
  // 계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "요청 계약"
  // -------------------------------------------------------------------------

  /**
   * POST /pbx-extensions/refresh — 호스트에서 등록 상태를 지금 다시 받아 목록으로 돌려준다
   * (`checked_at` 갱신). 503 PBX_EXTENSION_HOST_UNAVAILABLE 이면 기존 목록을 유지한다.
   * @param {string} companyId
   * @returns {Promise<PbxExtensionListResponse>}
   */
  refreshPbxExtensions(companyId) {
    return callApi.post(`${companyBase(companyId)}/pbx-extensions/refresh`, {});
  },

  /**
   * POST /pbx-extensions/:extId/release — retired·blocked 내선을 공용 풀로 돌려보낸다.
   * `:extId` 는 내선 id(상담사 번호 id 아님). 응답이 항목 전체가 아니라 호출부가 재조회한다.
   * @param {string} companyId
   * @param {string} extensionId
   * @returns {Promise<PbxExtensionReleaseResponse>}
   */
  releasePbxExtension(companyId, extensionId) {
    return callApi.post(
      `${companyBase(companyId)}/pbx-extensions/${encodeURIComponent(extensionId)}/release`,
      {}
    );
  },

  /**
   * POST /counselor-numbers/:id/extension/sync — PBX 재반영(4단계 라우트)
   * @param {string} companyId
   * @param {string} counselorNumberId
   * @returns {Promise<PbxExtensionActionResponse>}
   */
  syncCounselorExtension(companyId, counselorNumberId) {
    return callApi.post(`${counselorExtensionBase(companyId, counselorNumberId)}/sync`, {});
  },

  /**
   * POST /counselor-numbers/:id/extension/lock — 착·발신 즉시 차단(4단계 라우트)
   * @param {string} companyId
   * @param {string} counselorNumberId
   * @returns {Promise<PbxExtensionActionResponse>}
   */
  lockCounselorExtension(companyId, counselorNumberId) {
    return callApi.post(`${counselorExtensionBase(companyId, counselorNumberId)}/lock`, {});
  },

  /**
   * POST /counselor-numbers/:id/extension/unlock — 기본은 자격증명 재발급
   * @param {string} companyId
   * @param {string} counselorNumberId
   * @param {{ keep_credentials?: boolean }} [body]
   * @returns {Promise<PbxExtensionActionResponse>}
   */
  unlockCounselorExtension(companyId, counselorNumberId, body = {}) {
    return callApi.post(`${counselorExtensionBase(companyId, counselorNumberId)}/unlock`, body);
  },

  /**
   * POST /counselor-numbers/:id/extension/rotate-secret — SIP 비밀번호 회전
   * @param {string} companyId
   * @param {string} counselorNumberId
   * @returns {Promise<PbxExtensionActionResponse>}
   */
  rotateCounselorExtensionSecret(companyId, counselorNumberId) {
    return callApi.post(`${counselorExtensionBase(companyId, counselorNumberId)}/rotate-secret`, {});
  },

  /**
   * POST /counselor-numbers/:id/extension/disable — 일시 중지(상담사 번호 행 유지)
   * @param {string} companyId
   * @param {string} counselorNumberId
   * @returns {Promise<PbxExtensionActionResponse>}
   */
  disableCounselorExtension(companyId, counselorNumberId) {
    return callApi.post(`${counselorExtensionBase(companyId, counselorNumberId)}/disable`, {});
  },

  /**
   * POST /counselor-numbers/:id/extension/resume — 재개. 기본 자격증명 유지
   * @param {string} companyId
   * @param {string} counselorNumberId
   * @param {{ rotate_secret?: boolean }} [body]
   * @returns {Promise<PbxExtensionActionResponse>}
   */
  resumeCounselorExtension(companyId, counselorNumberId, body = {}) {
    return callApi.post(`${counselorExtensionBase(companyId, counselorNumberId)}/resume`, body);
  },

  /**
   * POST /counselor-numbers/:id/extension/check-sync — 전화기에 설정 재수신 요청.
   * `force` 는 통화 중(reason EXTENSION_IN_CALL)이라 사용자가 확인창에서 명시했을 때만.
   * @param {string} companyId
   * @param {string} counselorNumberId
   * @param {{ force?: boolean }} [body]
   * @returns {Promise<PbxCheckSyncResponse>}
   */
  checkSyncCounselorExtension(companyId, counselorNumberId, body = {}) {
    return callApi.post(`${counselorExtensionBase(companyId, counselorNumberId)}/check-sync`, body);
  },

  /**
   * GET /pbx-devices — 전화기 목록(미배정·연결·반납)
   * @param {string} companyId
   * @param {ListPbxDevicesParams} [params]
   * @returns {Promise<PbxDeviceListResponse>}
   */
  listPbxDevices(companyId, params = {}) {
    return callApi.get(`${companyBase(companyId)}/pbx-devices`, { params });
  },

  /**
   * POST /pbx-devices — MAC 수동 등록. 409 PBX_DEVICE_MAC_ALREADY_REGISTERED
   * @param {string} companyId
   * @param {CreatePbxDeviceRequest} payload
   * @returns {Promise<PbxDeviceItem>}
   */
  createPbxDevice(companyId, payload) {
    return callApi.post(`${companyBase(companyId)}/pbx-devices`, payload);
  },

  /**
   * PATCH /pbx-devices/:id — 라벨·설치 장소
   * @param {string} companyId
   * @param {string} deviceId
   * @param {UpdatePbxDeviceRequest} payload
   * @returns {Promise<PbxDeviceItem>}
   */
  updatePbxDevice(companyId, deviceId, payload) {
    return callApi.patch(`${deviceBase(companyId, deviceId)}`, payload);
  },

  /**
   * POST /pbx-devices/:id/claim — 상담사 번호의 내선에 전화기를 붙인다. 내선에 이미
   * 전화기가 있으면 409 PBX_EXTENSION_ALREADY_ASSIGNED — 확인 후 `replace: true` 로 재호출.
   * @param {string} companyId
   * @param {string} deviceId
   * @param {ClaimPbxDeviceRequest} payload
   * @returns {Promise<PbxDeviceActionResponse>}
   */
  claimPbxDevice(companyId, deviceId, payload) {
    return callApi.post(`${deviceBase(companyId, deviceId)}/claim`, payload);
  },

  /**
   * POST /pbx-devices/:id/unassign — 전화기에서 회선을 뗀다(내선 시크릿 회전 동반)
   * @param {string} companyId
   * @param {string} deviceId
   * @returns {Promise<PbxDeviceActionResponse>}
   */
  unassignPbxDevice(companyId, deviceId) {
    return callApi.post(`${deviceBase(companyId, deviceId)}/unassign`, {});
  },

  /**
   * DELETE /pbx-devices/:id — 반납(204). assigned 면 409 PBX_DEVICE_STATE_CONFLICT
   * @param {string} companyId
   * @param {string} deviceId
   * @returns {Promise<void>}
   */
  deletePbxDevice(companyId, deviceId) {
    return callApi.delete(deviceBase(companyId, deviceId));
  },

  /**
   * POST /pbx-devices/:id/rotate-admin-password — 전화기 관리자 비밀번호 재발급
   * @param {string} companyId
   * @param {string} deviceId
   * @returns {Promise<PbxDeviceItem>}
   */
  rotatePbxDeviceAdminPassword(companyId, deviceId) {
    return callApi.post(`${deviceBase(companyId, deviceId)}/rotate-admin-password`, {});
  },

  /**
   * GET /pbx-devices/:id/admin-password — 호출마다 감사 로그가 남는다
   * @param {string} companyId
   * @param {string} deviceId
   * @returns {Promise<{ admin_password: string }>}
   */
  getPbxDeviceAdminPassword(companyId, deviceId) {
    return callApi.get(`${deviceBase(companyId, deviceId)}/admin-password`);
  },

  /**
   * PATCH /pbx-devices/:id/transport — SIP 전송 전환(TLS↔UDP)
   * @param {string} companyId
   * @param {string} deviceId
   * @param {UpdatePbxDeviceTransportRequest} payload
   * @returns {Promise<PbxDeviceItem>}
   */
  updatePbxDeviceTransport(companyId, deviceId, payload) {
    return callApi.patch(`${deviceBase(companyId, deviceId)}/transport`, payload);
  },

  /**
   * POST /pbx-devices/:id/check-sync — 응답 형태는 내선 check-sync 와 같다
   * @param {string} companyId
   * @param {string} deviceId
   * @param {{ force?: boolean }} [body]
   * @returns {Promise<PbxCheckSyncResponse>}
   */
  checkSyncPbxDevice(companyId, deviceId, body = {}) {
    return callApi.post(`${deviceBase(companyId, deviceId)}/check-sync`, body);
  },

  /**
   * POST /pbx-devices/:id/recovery-code — 409 PBX_RECOVERY_CODE_ACTIVE 면 `replace: true` 재호출
   * @param {string} companyId
   * @param {string} deviceId
   * @param {{ replace?: boolean }} [body]
   * @returns {Promise<PbxRecoveryCodeResponse>}
   */
  createPbxDeviceRecoveryCode(companyId, deviceId, body = {}) {
    return callApi.post(`${deviceBase(companyId, deviceId)}/recovery-code`, body);
  },

  /**
   * GET /pbx-devices/:id/logs — 전화기가 올린 로그 목록
   * @param {string} companyId
   * @param {string} deviceId
   * @returns {Promise<PbxDeviceLogListResponse>}
   */
  listPbxDeviceLogs(companyId, deviceId) {
    return callApi.get(`${deviceBase(companyId, deviceId)}/logs`);
  },

  /**
   * GET /pbx-devices/:id/logs/:logId — 로그 파일 Blob
   * @param {string} companyId
   * @param {string} deviceId
   * @param {string} logId
   * @returns {Promise<Blob>}
   */
  async downloadPbxDeviceLog(companyId, deviceId, logId) {
    try {
      return await callApi.get(
        `${deviceBase(companyId, deviceId)}/logs/${encodeURIComponent(logId)}`,
        { responseType: "blob" }
      );
    } catch (err) {
      // responseType blob 이면 오류 본문도 Blob 으로 온다 — JSON 이면 풀어 둬야 서버 message·code 가 보인다
      const res = err?.response;
      const data = res?.data;
      if (res && data instanceof Blob && data.type.includes("json")) {
        try {
          res.data = JSON.parse(await data.text());
        } catch {
          // 본문이 깨졌으면 그대로 둔다 — 호출부 fallback 문구로 알린다
        }
      }
      throw err;
    }
  },

  /**
   * GET /pbx-sites — 설치 장소 목록(토큰 원문 없음)
   * @param {string} companyId
   * @returns {Promise<PbxSiteListResponse>}
   */
  listPbxSites(companyId) {
    return callApi.get(`${companyBase(companyId)}/pbx-sites`);
  },

  /**
   * POST /pbx-sites — 201 의 provisioning_url 은 이 응답에서만 온다
   * @param {string} companyId
   * @param {CreatePbxSiteRequest} payload
   * @returns {Promise<CreatePbxSiteResponse>}
   */
  createPbxSite(companyId, payload) {
    return callApi.post(`${companyBase(companyId)}/pbx-sites`, payload);
  },

  /**
   * PATCH /pbx-sites/:id
   * @param {string} companyId
   * @param {string} siteId
   * @param {UpdatePbxSiteRequest} payload
   * @returns {Promise<PbxSiteItem>}
   */
  updatePbxSite(companyId, siteId, payload) {
    return callApi.patch(siteBase(companyId, siteId), payload);
  },

  /**
   * DELETE /pbx-sites/:id — 204. 반납 안 된 전화기가 있으면 409 PBX_SITE_HAS_DEVICES
   * @param {string} companyId
   * @param {string} siteId
   * @returns {Promise<void>}
   */
  deletePbxSite(companyId, siteId) {
    return callApi.delete(siteBase(companyId, siteId));
  },

  /**
   * POST /pbx-sites/:id/rotate-token — 새 URL 1회. 409 PBX_SITE_ROTATION_IN_GRACE
   * @param {string} companyId
   * @param {string} siteId
   * @param {RotatePbxSiteTokenRequest} [body]
   * @returns {Promise<RotatePbxSiteTokenResponse>}
   */
  rotatePbxSiteToken(companyId, siteId, body = {}) {
    return callApi.post(`${siteBase(companyId, siteId)}/rotate-token`, body);
  },

  /**
   * POST /pbx-sites/:id/revoke-previous-token — 회전 없이 이전 토큰만 즉시 폐기
   * @param {string} companyId
   * @param {string} siteId
   * @returns {Promise<PbxSiteItem>}
   */
  revokePreviousPbxSiteToken(companyId, siteId) {
    return callApi.post(`${siteBase(companyId, siteId)}/revoke-previous-token`, {});
  },

  /**
   * GET /pbx-sites/:id/provisioning-url — 열람마다 감사 로그가 남는다
   * @param {string} companyId
   * @param {string} siteId
   * @returns {Promise<PbxProvisioningUrlResponse>}
   */
  getPbxSiteProvisioningUrl(companyId, siteId) {
    return callApi.get(`${siteBase(companyId, siteId)}/provisioning-url`);
  },

  // -------------------------------------------------------------------------
  // 콜봇 조직 목록 (call-api, CERTILIFE 전용) — 내선 관리 진입 화면의 병원 목록 원천
  // -------------------------------------------------------------------------

  /**
   * GET /api/v1/callbot/organizations — 콜봇 조직(vox 하위 조직)이 있는 회사 전부.
   * 요청: ~/md/back/콜봇-내선관리-조직목록-CERTILIFE-백엔드요청.md. 미배포 환경에서는 404 가 오고
   * 호출부가 inapi 전체 병원 목록으로 폴백한다.
   * @returns {Promise<{ items: Array<{ company_id: string, company_name: string | null, vox_organization_id: string, key_status: 'pending'|'active', verification_status: string | null, created_at: number | null, updated_at: number | null }> }>}
   */
  listCallbotOrganizations() {
    return callApi.get("/api/v1/callbot/organizations");
  },

  // -------------------------------------------------------------------------
  // 병원 목록 (inapi) — 조직 목록 라우트가 미배포일 때의 폴백. 이 메서드만 `@/axios` 인스턴스를 쓴다. 응답은 언랩되지 않으므로
  // 호출부가 `res.data`(`{ docs, totalDocs, totalPages, page }`)를 읽는다.
  // -------------------------------------------------------------------------

  /**
   * 병원 목록(페이지네이션). 병원 관리 화면(`HosManage.vue`)과 같은 `POST /companies/list/v2` 다.
   * 검색어 없이 전체를 돌려주므로 "목록 먼저 → 행 클릭" 화면에 쓴다(공용 `hospitals/search` 는
   * 검색어가 없으면 400 이라 목록용으로 못 쓴다). `docs[]._id` 가 곧 call-api 경로의 companyId 다.
   * @param {{ page: number, limit: number, search?: 'name'|'cNo'|'adminName', condition?: string }} params
   *   - search: 검색 카테고리, condition: 검색어. 둘 다 있을 때만 필터링된다.
   * @returns {Promise<import('axios').AxiosResponse<{ docs: Array<{ _id: string, name: string, cNo?: string, addrOne?: string, addrTwo?: string, addrThree?: string, adminName?: string }>, totalDocs: number, totalPages: number, page: number }>>}
   */
  listHospitals({ page, limit, search, condition }) {
    const body = { page, limit, type: "HOSPITAL", deleteYn: "N" };
    if (search && condition) {
      body.search = search;
      body.condition = condition;
    }
    return axios.post("/companies/list/v2", body);
  },
};
